import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { jwtLogin, jwtRegister } from "../lib/api";
import { useAuth } from "../lib/auth";
import GitHubStarsLink from "../components/GitHubStarsLink";

function formatErr(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AuthForm({ mode }) {
  // mode: "login" | "register"
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      mode === "register"
        ? await jwtRegister(email, password, name)
        : await jwtLogin(email, password);
      await refresh();
      navigate("/dashboard", { replace: true });
    } catch (ex) {
      setErr(formatErr(ex?.response?.data?.detail) || ex.message);
    } finally {
      setBusy(false);
    }
  };

  const isRegister = mode === "register";

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <nav className="border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 md:px-8 h-16 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2"
            data-testid="auth-brand"
          >
            <div className="w-6 h-6 bg-zinc-950 rounded-sm flex items-center justify-center">
              <span className="text-white font-mono text-[10px] font-bold">
                KS
              </span>
            </div>
            <span className="font-display font-semibold text-lg tracking-tight">
              Killswitch
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <GitHubStarsLink
              className="hidden lg:flex items-center gap-2 text-[15px] font-medium tracking-tight text-zinc-600 hover:text-zinc-950 transition-colors"
            />
            <Link
              to={isRegister ? "/login" : "/register"}
              className="text-sm text-zinc-600 hover:text-zinc-950 transition-colors"
              data-testid="auth-toggle"
            >
              {isRegister
                ? "Already have an account? Sign in →"
                : "New here? Create an account →"}
            </Link>
          </div>
        </div>
      </nav>

      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md fade-up">
          <div className="overline mb-3" data-testid="auth-overline">
            {isRegister ? "create your account" : "welcome back"}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tighter leading-[1.05] mb-2">
            {isRegister ? (
              <>
                Brief your idea.{" "}
                <span className="font-serif italic text-zinc-500">
                  Get the truth.
                </span>
              </>
            ) : (
              <>
                Sign in.{" "}
                <span className="font-serif italic text-zinc-500">
                  Stay honest.
                </span>
              </>
            )}
          </h1>
          <p className="text-zinc-600 mb-8">
            {isRegister
              ? "Two minutes to brief your first idea. One verdict that actually tells you something."
              : "Pick up where you left off."}
          </p>

          <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
            {isRegister && (
              <div>
                <label className="overline block mb-2">Name</label>
                <input
                  data-testid="auth-name"
                  type="text"
                  className="input-base"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jane Founder"
                  autoFocus
                />
              </div>
            )}
            <div>
              <label className="overline block mb-2">Email</label>
              <input
                data-testid="auth-email"
                type="email"
                required
                className="input-base"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@founder.co"
                autoFocus={!isRegister}
              />
            </div>
            <div>
              <label className="overline block mb-2">Password</label>
              <input
                data-testid="auth-password"
                type="password"
                required
                minLength={8}
                className="input-base"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={isRegister ? "min 8 chars" : "••••••••"}
              />
            </div>

            {err && (
              <div
                className="text-sm text-red-600 font-medium shake"
                data-testid="auth-error"
              >
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              className="btn btn-primary w-full"
              data-testid="auth-submit"
            >
              {busy ? (
                <>
                  <span className="spin inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full" />{" "}
                  {isRegister ? "Creating account…" : "Signing in…"}
                </>
              ) : isRegister ? (
                "Create account →"
              ) : (
                "Sign in →"
              )}
            </button>
          </form>
        </div>
      </main>
    </div>
  );
}
